import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { doc, setDoc, updateDoc, serverTimestamp, increment } from 'firebase/firestore'; // increment used in logic
import { db } from '../../../firebase';
import { getEnvironment } from '../../../utils/environment';
import { useAppContext } from '../../../contexts/AppContext';
import { APP_MODE } from '../../../constants/AppModes';

// Helper: Haversine
const deg2rad = (deg) => deg * (Math.PI / 180);
const rad2deg = (rad) => rad * (180 / Math.PI);

const getDistanceFromLatLonInKm = (lat1, lon1, lat2, lon2) => {
    var R = 6371;
    var dLat = deg2rad(lat2 - lat1);
    var dLon = deg2rad(lon2 - lon1);
    var a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    var d = R * c;
    return d;
}

// 🎯 [NEW] Calculate Bearing (0-360)
const calculateBearing = (lat1, lng1, lat2, lng2) => {
    const dLon = deg2rad(lng2 - lng1);
    const y = Math.sin(dLon) * Math.cos(deg2rad(lat2));
    const x = Math.cos(deg2rad(lat1)) * Math.sin(deg2rad(lat2)) -
        Math.sin(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.cos(dLon);
    let brng = Math.atan2(y, x);
    brng = rad2deg(brng);
    return (brng + 360) % 360;
};

// Helper: Get environment-aware user collection name
const getUserCollection = () => {
    const env = getEnvironment();
    if (env === 'production') return 'users';
    if (env === 'testnet' || env === 'sandbox') return 'testnet_users'; // 🎯 [Fix] Match Server Logic
    return 'dev_users'; // Only for local development
};

// const MAX_RETRIES = 50; // Unused

// 🎯 [UX] Global flag to ensure loading delay happens only ONCE per app session
let globalInitialLoadDone = false;

export const useSpotLocation = (piUser) => {
    const { t } = useTranslation();
    const {
        savedAgreements, // 🎯 [심플화] 약관 동의 상태만 사용
        savedGpsPermission, // 🎯 [Fix] DB에서 불러온 GPS 권한 상태
        isAuthInitializing, // 🎯 [근본 해결] 로그인 초기화 상태
        appMode // 🎯 [AppMode] 리소스 모드
    } = useAppContext();

    // 🎯 [AppMode] Ref Pattern to avoid re-triggering effects
    const appModeRef = useRef(appMode);
    useEffect(() => {
        appModeRef.current = appMode;
    }, [appMode]);

    const readCachedPos = () => {
        try {
            const cachedPos = JSON.parse(localStorage.getItem('last_known_pos'));
            if (cachedPos && Number.isFinite(Number(cachedPos.lat)) && Number.isFinite(Number(cachedPos.lng))) {
                return cachedPos;
            }
        } catch (_) { /* ignore */ }
        return null;
    };

    // 🎯 Location & Permission States
    // APPS→SPOT 재마운트 시 캐시를 동기로 복원해 지도가 서울 기본 좌표로 먼저 열리는 것을 막는다
    const [myPos, setMyPos] = useState(readCachedPos);
    const [locationTitle, setLocationTitle] = useState(t('spot_checking_permission'));
    const [permissionStatus, setPermissionStatus] = useState('prompt'); // 'prompt', 'granted', 'denied'
    const [permissionModal, setPermissionModal] = useState({ show: false, type: null }); // type: 'request' | 'denied'
    const [locationSource, setLocationSource] = useState('wifi'); // 'wifi' | 'gps'
    const [gpsToast, setGpsToast] = useState({ show: false, message: '' });

    // 🎯 [UX] Graceful Loading State
    // - 최초 실행 시에만 1초 지연을 두어 "Permission Flicker" 방지
    // - 리프레시나 탭 이동 후 복귀 시에는 지연 없음
    const [minDelayPassed, setMinDelayPassed] = useState(globalInitialLoadDone);

    useEffect(() => {
        if (globalInitialLoadDone) return;

        const timer = setTimeout(() => {
            console.log('[Permission] Graceful Loading Delay Complete');
            globalInitialLoadDone = true;
            setMinDelayPassed(true);
        }, 1000); // 1.0s Buffer (기본 깜빡임 방지용)

        return () => clearTimeout(timer);
    }, []);

    // 🎯 [심플화] Modal ready 조건: minDelay + 로그인 초기화 완료 + (agreements 로드 완료 OR 게스트)
    const isModalReady = useMemo(() => {
        if (!minDelayPassed) return false;
        if (isAuthInitializing) return false;
        if (!piUser) return true; // 게스트
        return savedAgreements !== undefined; // 로그인 유저: agreements 로드 완료 대기
    }, [piUser, savedAgreements, minDelayPassed, isAuthInitializing]);

    // Wait, I should not break the existing code structure. 
    // Line 41: const [gpsToast, setGpsToast] = useState({ show: false, message: '' });

    // 🎯 [Fix] useLatestRef pattern for piUser to solve closure staleness
    const piUserRef = useRef(piUser);
    useEffect(() => {
        piUserRef.current = piUser;
    }, [piUser]);

    const initialPosRef = useRef(null);

    // 🎯 [성능 개선] 두 위치 간 거리 계산 (미터)
    const getDistanceBetweenPositions = (pos1, pos2) => {
        if (!pos1 || !pos2) return Infinity;
        return getDistanceFromLatLonInKm(pos1.lat, pos1.lng, pos2.lat, pos2.lng) * 1000;
    };

    // Helper: Geolocator with Maximum Compatibility
    const getCurrentLocation = () => {
        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) {
                reject(new Error("Geolocation not supported"));
                return;
            }
            const options = {
                // 🎯 [FIX] Relax constraints for better success rate
                enableHighAccuracy: false, // Try basic WiFi/Cell first for speed
                timeout: 30000, // 30s
                maximumAge: 5 * 60 * 1000 // Accept cache up to 5 mins
            };
            navigator.geolocation.getCurrentPosition(
                (pos) => resolve(pos),
                (err) => {
                    console.error("Geo Error:", err.code, err.message);
                    if (err.code === err.TIMEOUT) {
                        console.log("Timeout, trying raw default...");
                        navigator.geolocation.getCurrentPosition(
                            (pos) => resolve(pos),
                            (finalErr) => reject(finalErr)
                        );
                    } else {
                        reject(err);
                    }
                },
                options
            );
        });
    };

    // 🎯 Main Location Strategy Logic
    // onLocationUpdate: (pos, isSilentUpdate) => Promise<void>
    // 🎯 [심플화] GPS 위치 획득만 수행 (ToS 체크는 App.js에서 처리)
    // 🎯 [FIX] Use watchPosition for continuous tracking
    const watchIdRef = useRef(null);
    const lastPosRef = useRef(null); // To calculate bearing

    const runInitialScanStrategy = useCallback((onLocationUpdate) => {
        let isMounted = true;

        const startGpsScan = async () => {
            if (!isMounted) return;

            // 🎯 [AppMode] 리소스 독점 작업 중이면 GPS 스캔 중단
            if (appModeRef.current !== APP_MODE.NORMAL) {
                console.log('[Location] 리소스 독점 모드(HEAVY_TASK)로 인해 GPS 스캔 건너뜀');
                return;
            }

            try {
                console.log('[Location] GPS 실시간 추적 시작 (watchPosition)');

                // 캐시된 위치 먼저 적용 (빠른 시작)
                const cachedPosStr = localStorage.getItem('last_known_pos');
                if (cachedPosStr) {
                    try {
                        const cachedPos = JSON.parse(cachedPosStr);
                        if (cachedPos.lat && cachedPos.lng) {
                            console.log('[Location] 캐시된 위치 사용:', cachedPos);
                            setMyPos(cachedPos);
                            lastPosRef.current = cachedPos;
                            setLocationTitle(`Lat: ${cachedPos.lat.toFixed(4)}\nLng: ${cachedPos.lng.toFixed(4)} (Cached)`);
                            if (onLocationUpdate) onLocationUpdate(cachedPos);
                        }
                    } catch (e) {
                        console.warn('[Location] 캐시 파싱 실패:', e);
                    }
                }

                // GPS 위치 추적 (watchPosition)
                if (navigator.geolocation) {
                    // Clear existing watch if any
                    if (watchIdRef.current !== null) {
                        navigator.geolocation.clearWatch(watchIdRef.current);
                    }

                    watchIdRef.current = navigator.geolocation.watchPosition(
                        (position) => {
                            if (!isMounted) return;

                            // 🎯 [AppMode] Check
                            if (appModeRef.current !== APP_MODE.NORMAL) {
                                // console.log('[Location] GPS Update Skipped (Heavy Task)');
                                return;
                            }

                            const currentLat = position.coords.latitude;
                            const currentLng = position.coords.longitude;
                            const currentTime = Date.now();

                            // Calculate Heading & Speed
                            let heading = 0;
                            let speed = position.coords.speed; // m/s

                            if (lastPosRef.current) {
                                const dist = getDistanceFromLatLonInKm(lastPosRef.current.lat, lastPosRef.current.lng, currentLat, currentLng) * 1000;
                                const timeDiff = (currentTime - (lastPosRef.current.timestamp || currentTime)) / 1000; // seconds

                                // Only update heading if moved > 2 meters (filtering jitter)
                                if (dist > 2) {
                                    heading = calculateBearing(lastPosRef.current.lat, lastPosRef.current.lng, currentLat, currentLng);

                                    // 🎯 [Fix] Calculate Manual Speed if device speed is null
                                    if (speed === null && timeDiff > 0) {
                                        speed = dist / timeDiff;
                                        // console.log(`[Location] Manual Speed Calc: ${speed.toFixed(2)} m/s (${dist.toFixed(1)}m / ${timeDiff.toFixed(1)}s)`);
                                    }

                                } else if (lastPosRef.current.heading) {
                                    heading = lastPosRef.current.heading; // Keep previous heading
                                    if (speed === null) speed = 0; // Assume stopped if dist <= 2
                                }
                            }

                            const gpsPos = {
                                lat: currentLat,
                                lng: currentLng,
                                acc: position.coords.accuracy,
                                speed: speed || 0, // 🎯 [Fix] Ensure number
                                heading: position.coords.heading || heading || 0, // 🎯 [Fix] Prefer native GPS heading
                                timestamp: currentTime
                            };

                            // Save Last Pos
                            lastPosRef.current = gpsPos;
                            localStorage.setItem('last_known_pos', JSON.stringify(gpsPos));

                            setLocationSource('gps');
                            setMyPos(gpsPos);
                            setLocationTitle(`Lat: ${gpsPos.lat.toFixed(4)}\nLng: ${gpsPos.lng.toFixed(4)}`);
                            setPermissionStatus('granted');

                            // 🎯 Notify Controller (Controller will filter by distance)
                            if (onLocationUpdate) onLocationUpdate(gpsPos);

                            // console.log('[Location] GPS Update:', gpsPos);
                        },
                        (error) => {
                            if (!isMounted) return;
                            console.warn('[Location] GPS 오류:', error.message);

                            if (error.code === 1) { // PERMISSION_DENIED
                                setPermissionStatus('denied');
                                setLocationTitle(t('spot_permission_denied'));
                            } else {
                                // 타임아웃/이용불가 - 캐시 위치 유지
                                setLocationSource('wifi');
                            }
                        },
                        {
                            enableHighAccuracy: true,
                            timeout: 30000,
                            maximumAge: 0 // No Cache for Watch
                        }
                    );
                } else {
                    console.warn('[Location] Geolocation API 미지원');
                    setLocationTitle(t('spot_location_error'));
                }

            } catch (err) {
                console.error('[Location] 스캔 로직 오류:', err);
            }
        };

        // 바로 GPS 스캔 시작
        startGpsScan();

        return () => {
            isMounted = false;
            // Cleanup watch
            if (watchIdRef.current !== null) {
                navigator.geolocation.clearWatch(watchIdRef.current);
            }
        };
    }, []); // 의존성 없음 - GPS만 수행

    // 🎯 [Fix] Auto-start scan when savedGpsPermission loads is handled by SpotGoMain calling this logic?
    // Actually, we removed internal useEffect to avoid double call.
    // SpotGoMain will trigger this when isModalReady becomes true.
    // But SpotGoMain doesn't know about isModalReady changes implicitly unless we return it.
    // We return isModalReady. SpotGoMain's useEffect depends on it? No, it depends on runInitialScanStrategy.
    // runInitialScanStrategy changes when savedGpsPermission changes.
    // runInitialScanStrategy changes when savedGpsPermission changes.

    // NOTE: We removed the internal useEffect that was here.
    const hasScanStartedRef = useRef(false);
    // Logic moved to SpotGoMain or managed via runInitialScanStrategy invocation context.

    // Trigger Manual Refresh (e.g. Refresh Btn)
    const refreshLocation = async () => {
        // 🎯 [AppMode] 리소스 독점 작업 중이면 새로고침 차단
        if (appModeRef.current !== APP_MODE.NORMAL) {
            console.log('[Location] 리소스 독점 모드(HEAVY_TASK)로 인해 새로고침 차단');
            return null;
        }

        try {
            const pos = await getCurrentLocation();

            // 🎯 [AppMode] 비동기 완료 후 체크
            if (appModeRef.current !== APP_MODE.NORMAL) return null;

            const newPos = { lat: pos.coords.latitude, lng: pos.coords.longitude };
            setMyPos(newPos);
            setLocationTitle(`Lat: ${newPos.lat.toFixed(4)}\nLng: ${newPos.lng.toFixed(4)}`);
            return newPos;
        } catch (e) {
            throw e;
        }
    };

    return {
        // States
        myPos,
        setMyPos,
        locationTitle,
        setLocationTitle,
        permissionStatus,
        setPermissionStatus,
        permissionModal,
        setPermissionModal,
        locationSource,
        setLocationSource,
        gpsToast,
        setGpsToast,
        isModalReady, // 🎯 [Fix] Delay flag for modal rendering
        isCheckingDb: piUser && savedGpsPermission === undefined, // 🎯 [UI] DB 권한 로딩 중 상태

        // Refs
        initialPosRef,

        // Functions
        runInitialScanStrategy,
        refreshLocation,
        getCurrentLocation,
        // getHighAccuracyLocation, // Removed as unused
        getDistanceBetweenPositions
    };
};
